import axios from "axios";

/**
 * This function handles the event when a user clicks on the "compare" button for a product, stream, or fast.
 * It sends an HTTP PATCH request to the appropriate endpoint to toggle the compare status of the item.
 * If the request is successful, it updates the UI to reflect the new compare status.
 * @param {Event} event - The click event on the "compare" button
 */
const compare = (event) => {
	// Get the button element and the ID of the item to be compared
	const PRODUCT_ID = event.currentTarget.dataset.productId;
	const CATEGORY_ID = event.currentTarget.dataset.categoryId;

	// Define if the user wants to add the product to comparison list
	// or wants to remove, by checking if the product exists in comparison list.
	if (existsInComparison(PRODUCT_ID, CATEGORY_ID)) {
		// If it exists the DELETE it.
		axios
			.delete(route("comparables.remove", PRODUCT_ID))
			.then((response) => {
				// If there was an error in the response data, display a toast message with the error message and return
				if (response.data.error) {
					Toast.danger(response.data.message);
					return;
				}
				if (response.status === 202) {
					updateComparableStorage(response.data.data.comparables);
					setComparables();
					window.dispatchEvent(
						new CustomEvent("remove-from-comparables", {
							detail: {
								product_id: PRODUCT_ID,
								category_id: CATEGORY_ID,
							},
						})
					);
				}
			})
			.catch((error) => {
				console.log(error);
				// If there was an error in the response data, display a toast message with the error message
				if (error.response.data.error) {
					Toast.danger(error.response.data.message);
					return;
				}
			});
	} else {
		// If it doesn't exist, PATCH it.
		axios
			.patch(route("comparables.add", PRODUCT_ID))
			.then((response) => {
				// If there was an error in the response data, display a toast message with the error message and return
				if (response.data.error) {
					Toast.danger(response.data.message);
					return;
				}
				if (response.status === 201) {
					updateComparableStorage(response.data.data.comparables);
					setComparables();
					window.dispatchEvent(
						new CustomEvent("add-to-comparables", {
							detail: {
								product_id: PRODUCT_ID,
								category_id: CATEGORY_ID,
							},
						})
					);
				}
			})
			.catch((error) => {
				console.log(error);
				// If there was an error in the response data, display a toast message with the error message
				if (error.response.data.error) {
					Toast.danger(error.response.data.message);
					return;
				}
			});
	}
};

/**
 * Update the UI by itterating throught the Comparables
 * inside the sessionStorage.
 * @returns {void}
 */
const setComparables = () => {
	// Clear all buttons.
	clearUI();
	if (sessionStorage.getItem("comparables")) {
		let comparables = JSON.parse(sessionStorage.getItem("comparables"));
		// Iterate over the Each Category's Comparables and update the UI
		Object.keys(comparables).forEach((categoryId) => {
			// If there is not comparable button for this category, skip!
			if (
				document.querySelector(
					`[data-category-id="${categoryId}"][data-comparable-id]`
				) === null
			) {
				return;
			}
			comparables[categoryId].forEach((productId) => {
				// Select all and iterate in case there are more than
				// one buttons representing the Compare Action of the same
				// Product.
				let compareButtons = document.querySelectorAll(
					`[data-comparable-id="${productId}"]`
				);
				if (compareButtons.length > 0) {
					compareButtons.forEach((button) => {
						button.setAttribute("aria-pressed", true);
						button.setAttribute(
							"title",
							"Αφαίρεση από τη Σύγκριση"
						);
					});
				}
			});
		});
	}
};

/**
 * Request a fresh comparison list from the server.
 * Upon recieving the response, it calls updateComparableStorage
 * to sync the sessionStorage of JS and setComparables to make the
 * UI reflect the changes.
 * @returns {void}
 */
const getComparables = () => {
	axios
		.get(route("comparables.index"))
		.then((response) => {
			// Check if the response was successful
			if (response.status === 200 && response.data.data.comparables) {
				// Check if there are any comparables
				updateComparableStorage(response.data.data.comparables);
			}
		})
		.then((response) => {
			// Update the UI to reflect changes
			setComparables();
		})
		.catch((error) => {
			// Handle errors
			console.error("Error while getting comparables:", error);
		});
};

/**
 *
 * Private Helper Functions
 *
 */

/**
 * Determine if the Product already exists in Comparison list.
 * @param {string} productId - The ID of Product
 * @param {string} categoryId - The ID of the Category
 * @returns {bool} - True if product exists in comparison || False if product doesn't exist
 */
const existsInComparison = (productId, categoryId) => {
	const PRODUCT_ID = String(productId);
	const CATEGORY_ID = String(categoryId);

	// Retrieve existing comparison list from session storage
	if (sessionStorage.getItem("comparables")) {
		let comparables = JSON.parse(sessionStorage.getItem("comparables"));

		// Check if there is a comparable list for this Category.
		if (comparables[CATEGORY_ID]) {
			// Check if this Product exists in this Category's Comparables.
			return comparables[CATEGORY_ID].includes(PRODUCT_ID);
		} else {
			// Product doesn't exist in Comparables.
			return false;
		}
	} else {
		// Product doesn't exist in Comparables.
		return false;
	}
};

/**
 * Clean the UI by reseting all buttons' state.
 */
const clearUI = () => {
	let compareButtons = document.querySelectorAll(`[data-comparable-id]`);
	if (compareButtons.length > 0) {
		compareButtons.forEach((button) => {
			button.setAttribute("aria-pressed", false);
			button.setAttribute("title", "Προσθήκη στη Σύγκριση");
		});
	}
};

/**
 * Update the sessionStorage in JS to reflect the PHP Session.
 * The UI is depending on this storage to decide upon the type of the
 * request to send to the Server to update the content of Comparison List.
 * Also the UI is depending on this storage to change the appearence of
 * the comparison buttons to reflect the state (where the product is inside
 * a comparison list or not.
 * @param {object} comparables - An Object that is coming from the Response of the Request
 * @returns {void}
 */
const updateComparableStorage = (comparables) => {
	// Check that the 'comparables' coming from the response has actually content.
	if (comparables && Object.keys(comparables).length) {
		// Store comparables in session storage.
		sessionStorage.setItem("comparables", JSON.stringify(comparables));
	} else {
		// Empty the storage.
		sessionStorage.removeItem("comparables");
	}
};

export { compare, setComparables, getComparables };
